import { JsonDataValue } from '../utils';
import { ShopifyRecipeInformation } from './shopify';

export type CustomerAddress = {
  streetAddress: string;
  locality: string;
  postalCode: string;
  country: string;
};

export type OrderCustomer = {
  customerId: string;
  customerName: string;
  birthDate?: string;
  email?: string;
  firstName?: string;
  doctorId?: string;
};

export type OrderCustomerDetails = OrderCustomer & {
  gender: string;
  givenName: string;
  familyName: string;
  email: string;
  address: CustomerAddress;
  birthDate: string;
};

export type OrderProduct = {
  productId: string;
  productName: string;
  unit: string;
  amount: number;
  recipeInformation?: ShopifyRecipeInformation | ShopifyRecipeInformation[];
  isOnlyRecipe?: boolean;
};

export type OrderPayment = {
  paymentId: string;
  subscriptionId?: string;
  isSubscription?: boolean;
  compoundAmount?: number;
  productTitle?: string;
  orderId?: string;
  orderName?: string;
};

export type ProductAllocation = {
  productId: string;
  productName: string;
  productType: string;
  description: string;
  unit: string;
  expireInMonth: number;
};

export type OrderProductDetails = OrderProduct & {
  productGroupId: string;
  allocation: ProductAllocation[];
};

export type SurveyAnswer = {
  question: string;
  /**
   * A single answer, or one entry per selection for MultiSelectAnswer - that
   * step has always stored an array. Render it through a formatter rather than
   * directly: React concatenates an array with no separator.
   */
  answer: string | string[];
  type: string;
  /** Semantic tag copied from the step, see `Templates.StepDataBase.searchID`. */
  searchID?: string;
};

/**
 * A survey answer as a single display string, one selection per line.
 *
 * MultiSelectAnswer stores one entry per selection, and a raw array reaches a
 * consumer as whatever that consumer's stringifier does with it - AppSync's
 * `String!` gives "a,b", React concatenates with no separator at all. Answer
 * labels routinely contain commas ("Ja, mein Vater"), so entries are separated
 * by a newline rather than a comma: nothing in a label can be mistaken for the
 * separator. Nested selections already arrive grouped per parent, so one line
 * reads "Mein Großvater - Väterlicherseits/Mütterlicherseits".
 *
 * Whatever renders this has to keep the newlines - in HTML that means
 * `white-space: pre-line`, which both admin call sites set.
 *
 * Frontend only. Lambdas cannot call this: they resolve this package from the
 * prebuilt inHouse layer zip, where a newly added export does not exist yet.
 * `actions-request.ts` keeps a local copy of the same rule for that reason.
 */
export const formatSurveyAnswer = (answer: string | string[] | undefined): string =>
  Array.isArray(answer) ? answer.filter(Boolean).join('\n') : (answer ?? '');

export type OrderAssets = {
  survey: JsonDataValue;
  answers: JsonDataValue;
  images: string[];
};

export enum OrderRequestState {
  UNKNOWN = 'UNKNOWN',
  IDLE = 'IDLE',
  ALLOWED = 'ALLOWED',
  DENIED = 'DENIED',
  FAILED = 'FAILED',
}

export enum SurveyAssetCategory {
  JSON_TEMPLATE = 'JSON_TEMPLATE',
  JSON_ANSWERS = 'JSON_ANSWERS',
  JSON_IMAGES = 'JSON_IMAGES',
  IMAGE = 'IMAGE',
}

export type SurveyAsset = {
  key: string;
  size: number;
  category: SurveyAssetCategory;
  thumbnail?: string;
  signedUrl?: string;
};

export type RequestAssignedDoctor = {
  doctorId: string;
  factor: string;
  factor2: string;
  fee: string;
  fee2: string;
};

export type OrderRequest = {
  requestId: string;
  name: string;
  templateId: string;
  customer: OrderCustomer;
  product: OrderProduct;
  orderState: OrderRequestState;
  assets: SurveyAsset[];
  processed: boolean;
  fromShopify?: boolean;
  isFollowup?: boolean;
  payment: OrderPayment;
  comment: string;
  sproofDoctorLink: string;
  sproofPharmacyLink: string;
  history: { event: RequestEvents; time: number }[];
  assignedDoctor?: RequestAssignedDoctor;
};

export type OrderFollowupRequest = {
  requestId: string;
  name: string;
  templateId: string;
  customer: OrderCustomer;
  product: OrderProduct;
  orderState: OrderRequestState;
  assets: SurveyAsset[];
  processed: boolean;
  paymentId: string;
  comment: string;
  sproofDoctorLink: string;
  sproofPharmacyLink: string;
  history: { event: RequestEvents; time: number }[];
  isVisible: boolean;
  assignedDoctor?: RequestAssignedDoctor;
  fromShopify: boolean;
  orderId?: string;
};

export type OrderRequestShort = {
  requestId: string;
  name: string;
  templateId: string;
  customerId: string;
  createdAt: number;
  orderState: OrderRequestState;
};

export type OrderRequestDetails = OrderRequest & {
  customer: OrderCustomerDetails;
  qanda: SurveyAnswer[];
  isSubscription: boolean;
};

export type OrderFollowupRequestDetails = OrderFollowupRequest & {
  customer: OrderCustomerDetails;
  qanda: SurveyAnswer[];
  isSubscription: boolean;
};

export type OrderRequestParams = {
  surveyTag: string;
  assets: OrderAssets;
  productId: string;
  customerId: string;

  paymentId: string;
};

export type OrderRequestEvent = {
  requestId: string;
  userId: string;
  action: string;
  source: string;
};
export enum SurveySubject {
  ALLOW_ORDER_REQUEST = 'ALLOW_ORDER_REQUEST',
  DENY_ORDER_REQUEST = 'DENY_ORDER_REQUEST',
}

export type TreatmentResponse = {
  treatmentId: string;
  treatmentType: 'SINGLE' | 'MULTIPLE';
  expiryDate: number;
  consumableAmount: number;
  consumedAmount: number;
};

export type OrderRequestResponse = {
  surveyTag: string;
  state: OrderRequestState;
  previousState: OrderRequestState;
  treatment?: TreatmentResponse;
};

export enum RequestEvents {
  REZEPT_GENERATE = 'RezeptGenerate',
  REZEPT_SIGN = 'RezeptSign',
  REQUEST_GENERATE = 'RequestGenerate',
  INVOICE_GENERATE = 'InvoiceGenerate',
  CHARGE_PAYED = 'ChargePayed',
  CHARGE_FAILED = 'ChargeFailed',
  CHARGE_REFUND = 'ChargeRefund',
}
